<script lang="ts">
import CloseIcon from '../icons/Close.svelte';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  children?: any;
  footer?: any;
  showCloseButton?: boolean;
};

let {
  isOpen = false,
  onClose,
  title,
  subtitle,
  size = 'lg',
  children,
  footer,
  showCloseButton = true
}: Props = $props();

// Handle escape key
function handleKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape' && isOpen) {
    onClose();
  }
}

// Handle backdrop click
function handleBackdropClick(event: MouseEvent) {
  onClose();
}

// Stop propagation when clicking inside modal
function handleContentClick(event: MouseEvent) {
  event.stopPropagation();
}
</script>

<svelte:window onkeydown={handleKeydown} />

{#if isOpen}
  <div class="modal-backdrop" onclick={handleBackdropClick}>
    <div class="modal-content {size}" onclick={handleContentClick}>
      {#if title || showCloseButton}
        <div class="modal-header">
          <div>
            {#if title}
              <h2>{title}</h2>
            {/if}
            {#if subtitle}
              <p class="subtitle">{subtitle}</p>
            {/if}
          </div>
          {#if showCloseButton}
            <button class="close-button" onclick={onClose} aria-label="Close">
              <CloseIcon />
            </button>
          {/if}
        </div>
      {/if}

      <div class="modal-body">
        {@render children?.()}
      </div>

      {#if footer}
        <div class="modal-footer">
          {@render footer?.()}
        </div>
      {/if}
    </div>
  </div>
{/if}

<style lang="scss">
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: var(--z-modal);
  padding: var(--spacing-lg);
  backdrop-filter: blur(2px);
  animation: fadeIn 0.2s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.modal-content {
  background: var(--color-background);
  border-radius: var(--radius-lg);
  width: 100%;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: var(--shadow-deep);
  border: var(--border-width-thin) solid var(--color-border);
  animation: slideUp 0.2s ease-out;

  &.sm {
    max-width: 500px;
  }

  &.md {
    max-width: 800px;
  }

  &.lg {
    max-width: 1000px;
  }

  &.xl {
    max-width: 1400px;
  }
}

@keyframes slideUp {
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: var(--spacing-xl) var(--spacing-2xl);
  border-bottom: var(--border-width-thin) solid var(--color-border);
  background: var(--color-background-secondary);
  border-radius: var(--radius-lg) var(--radius-lg) 0 0;

  h2 {
    margin: 0 0 var(--spacing-xs) 0;
    font-size: var(--font-size-2xl);
    font-weight: var(--font-weight-semibold);
    color: var(--color-text-primary);
  }

  .subtitle {
    margin: 0;
    font-size: var(--font-size-sm);
    color: var(--color-text-secondary);
  }
}

.close-button {
  background: transparent;
  border: none;
  padding: var(--spacing-xs);
  cursor: pointer;
  color: var(--color-text-tertiary);
  transition: color var(--transition-fast);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-sm);

  &:hover {
    color: var(--color-text-primary);
    background: var(--color-background-tertiary);
  }
}

.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: var(--spacing-2xl);
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: var(--spacing-md);
  padding: var(--spacing-xl) var(--spacing-2xl);
  border-top: var(--border-width-thin) solid var(--color-border);
  background: var(--color-background-secondary);
  border-radius: 0 0 var(--radius-lg) var(--radius-lg);
}

@media (max-width: 768px) {
  .modal-backdrop {
    padding: var(--spacing-md);
  }

  .modal-content {
    max-height: 90vh;

    &.sm, &.md, &.lg, &.xl {
      max-width: 100%;
    }
  }

  .modal-header,
  .modal-body,
  .modal-footer {
    padding: var(--spacing-lg);
  }
}
</style>
